/**
 * Code Status Checker Component
 * Allows admins to check the status of a specific redeem code
 * Shows validation status, redemption metadata, and timestamps
 */

import { useState } from 'react';
import { useCodeStatus } from '../../hooks/useRedeemGroupApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { formatDate } from '../../lib/utils/formatters';

export function CodeStatusChecker() {
  const [inputCode, setInputCode] = useState('');
  const [queryCode, setQueryCode] = useState('');

  const { data, isLoading, error } = useCodeStatus(queryCode);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCode = inputCode.trim().toUpperCase();
    if (trimmedCode) {
      setQueryCode(trimmedCode);
    }
  };

  const handleClear = () => {
    setInputCode('');
    setQueryCode('');
  };

  // Determine if we have a 404 error (invalid code) vs other errors
  // The authenticated-api throws Error with message format: "HTTP 404: ..."
  // React Query wraps this in error.cause, so check both error.message and error.cause.message
  const errorMessage = error instanceof Error ? error.message : String(error);
  const causeMessage =
    error && 'cause' in error && error.cause instanceof Error
      ? error.cause.message
      : '';
  const is404Error =
    error &&
    (errorMessage.includes('HTTP 404') ||
      errorMessage.includes('404') ||
      causeMessage.includes('HTTP 404') ||
      causeMessage.includes('404'));
  const isNetworkError = error && !is404Error;

  return (
    <div className="space-y-4">
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="flex-1">
          <label htmlFor="code-input" className="sr-only">
            Enter redeem code
          </label>
          <input
            id="code-input"
            type="text"
            value={inputCode}
            onChange={e => setInputCode(e.target.value)}
            placeholder="Enter code to check status..."
            aria-describedby={queryCode ? 'code-status-result' : undefined}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={!inputCode.trim() || isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Checking...' : 'Check Status'}
        </button>
        {queryCode && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            Clear
          </button>
        )}
      </form>

      {/* Status Display */}
      {queryCode && (
        <div
          id="code-status-result"
          data-testid="code-status-result"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="md" />
            </div>
          ) : is404Error ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 mb-3">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                Invalid Code
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The code &quot;{queryCode}&quot; does not exist in the system.
              </p>
            </div>
          ) : isNetworkError ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900 mb-3">
                <svg
                  className="w-6 h-6 text-yellow-600 dark:text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                Network Error
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Failed to check code status. Please try again.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                {String(error)}
              </p>
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Valid/Invalid Badge */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Code Status
                </h3>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Valid
                </span>
              </div>

              {/* Metadata - wrapped in dl for semantic HTML */}
              <dl className="space-y-4">
                {/* Code Display */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Code
                  </dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-gray-100">
                    {data.code}
                  </dd>
                </div>

                {/* Group ID */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Group ID
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {data.groupId}
                  </dd>
                </div>

                {/* Redeemed Status */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Redeemed
                  </dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        data.redeemed
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {data.redeemed ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>

                {/* Redeemed By */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Redeemed By
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {data.redeemedBy || '-'}
                  </dd>
                </div>

                {/* Redeemed At */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Redeemed At
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(data.redeemedAt)}
                  </dd>
                </div>

                {/* Generated At */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Generated At
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(data.generated_at)}
                  </dd>
                </div>

                {/* Granted Items - only show for redeemed codes with snapshot data.
                    Compared with > 0: two empty arrays would otherwise render a
                    bare "0". */}
                {data.redeemed &&
                  ((data.grantedEntitlementIds?.length ?? 0) > 0 ||
                    (data.grantedBadgeIds?.length ?? 0) > 0) && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Items Granted
                      </dt>
                      <dd className="mt-1">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-900 dark:text-gray-100">
                          {data.grantedEntitlementIds?.map(id => (
                            <li key={`ent-${id}`}>Entitlement: {id}</li>
                          ))}
                          {data.grantedBadgeIds?.map(id => (
                            <li key={`badge-${id}`}>Badge: {id}</li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}
              </dl>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
